/* eslint-disable max-len */
/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import { FiActivity, FiBarChart, FiDollarSign, FiGift, FiHeart, FiUsers } from 'react-icons/fi'
import { IoClose } from 'react-icons/io5'
import groupApi from '_api/group'
import landingApi from '_api/landing'
import reportApi from '_api/report'
import GuideRequestForm from './GuideRequestForm'
import LandingFooter from './components/LandingFooter'
import LandingNav from './components/LandingNav'
import GroupBuySection from './components/GroupBuySection'
import HeroSection from './components/HeroSection'
import HowItWorksSection from './components/HowItWorksSection'
import PartnerSection from './components/PartnerSection'
import StatsSection from './components/StatsSection'
import StorySection from './components/StorySection'
import './style.scss'

const LandingPage = () => {
  const history = useHistory()
  const { addToast } = useToasts()
  const [stats, setStats] = useState({
    totalEvents: 0,
    totalMembers: 0,
    totalGroups: 0,
  })
  const [partnerForm, setPartnerForm] = useState({
    name: '',
    email: '',
    message: '',
  })
  const [isJoinHostVisible, setIsJoinHostVisible] = useState(false)
  const [mapGroups, setMapGroups] = useState([])

  useEffect(() => {
    reportApi.countData().then((res) => {
      setStats(res?.msgResp || { totalEvents: 0, totalMembers: 0, totalGroups: 0 })
    })
  }, [])

  useEffect(() => {
    groupApi
      .getListGroupsMap()
      .then((res) => {
        setMapGroups(res?.msgResp || [])
      })
      // The map still renders the island without pins if the list can't be loaded
      .catch(() => setMapGroups([]))
  }, [])

  useEffect(() => {
    const reveals = Array.from(document.querySelectorAll('.landing-reveal'))

    if (!reveals.length) return undefined

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible')
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.12 }
    )

    reveals.forEach((element, index) => {
      element.style.setProperty('--reveal-delay', `${index * 80}ms`)
      observer.observe(element)
    })

    return () => observer.disconnect()
  }, [isJoinHostVisible])

  const howItWorksSteps = [
    {
      number: '01',
      title: 'Join a Group',
      description: 'Find your people. Join a local group.',
    },
    {
      number: '02',
      title: 'Browse Deals',
      description: "Deals on food, essentials and more. Grab a spot before it's gone.",
    },
    {
      number: '03',
      title: 'Save Together',
      description: 'Group filled. Order placed. Savings delivered.',
    },
  ]

  const partnerBenefits = useMemo(
    () => [
      {
        icon: <FiActivity />,
        title: 'Instant Market Reach',
        description: `Tap into ${stats.totalMembers.toLocaleString()}+ active buyers across ${stats.totalGroups.toLocaleString()} community groups.`,
      },
      {
        icon: <FiGift />,
        title: 'Bulk Orders Guaranteed',
        description:
          'Every campaign is fulfilled only when the group target is met, so volume comes first.',
      },
      {
        icon: <FiDollarSign />,
        title: 'Lower Cost to Sell',
        description:
          'One consolidated order means fewer deliveries, simpler logistics, and more margin.',
      },
      {
        icon: <FiUsers />,
        title: 'We Handle the Community',
        description:
          'Co-bee manages coordination, payments, and communication so you can focus on your products.',
      },
      {
        icon: <FiBarChart />,
        title: 'Real Buyer Insights',
        description:
          'Get clear demand signals from each campaign and plan inventory with better confidence.',
      },
      {
        icon: <FiHeart />,
        title: 'Grow With the Community',
        description:
          'Build repeat buyers, referrals, and long-term advocates through trusted community hosts.',
      },
    ],
    [stats.totalGroups, stats.totalMembers]
  )

  const handleJoinNow = () => {
    history.push('/sign-up')
  }

  const handleBecomeHost = () => {
    setIsJoinHostVisible(true)

    window.requestAnimationFrame(() => {
      document.getElementById('host-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  const handlePartnerFormChange = ({ target: { name, value } }) => {
    setPartnerForm((prev) => ({ ...prev, [name]: value }))
  }

  const handlePartnerFormSubmit = async (event) => {
    event.preventDefault()

    try {
      const response = await landingApi.createRequireContact(partnerForm)

      if (response.msgCode === 70300) {
        addToast('Thank you for your interest! We will get back to you soon.', {
          appearance: 'success',
          autoDismiss: true,
        })
        setPartnerForm({ name: '', email: '', message: '' })
      }
    } catch (error) {
      addToast(
        error?.msgResp || 'There was an error submitting your message. Please try again later.',
        {
          appearance: 'error',
          autoDismiss: true,
        }
      )
    }
  }

  return (
    <div className='landing-page-v2'>
      <LandingNav />

      <main>
        <HeroSection
          stats={stats}
          mapGroups={mapGroups}
          onJoinMember={handleJoinNow}
          onBecomeHost={handleBecomeHost}
        />

        {isJoinHostVisible && (
          <section className='host-form-section' id='host-form'>
            <div className='landing-shell'>
              <div className='host-form-card landing-reveal'>
                <div className='host-form-header'>
                  <div>
                    <p className='section-label section-label--teal'>Become a host</p>
                    <h2 className='section-title section-title--dark'>
                      Start your own community campaign
                    </h2>
                  </div>
                  <button
                    type='button'
                    className='host-form-close'
                    aria-label='Close host form'
                    onClick={() => setIsJoinHostVisible(false)}
                  >
                    <IoClose />
                  </button>
                </div>
                <GuideRequestForm />
              </div>
            </div>
          </section>
        )}

        <GroupBuySection />
        <HowItWorksSection steps={howItWorksSteps} />
        <StatsSection stats={stats} />
        <StorySection />
        <PartnerSection
          benefits={partnerBenefits}
          partnerForm={partnerForm}
          onPartnerFormChange={handlePartnerFormChange}
          onPartnerFormSubmit={handlePartnerFormSubmit}
        />
      </main>

      <LandingFooter />
    </div>
  )
}

export default LandingPage
